import axios from 'axios';

const API_BASE_URL = 'https://api.tabtrips.com/olivrweb/hotelapi/adminPanel.php';
// Cookie utility function
const getCookie = (name) => {
  const nameEQ = name + '=';
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) {
      try {
        return decodeURIComponent(c.substring(nameEQ.length, c.length));
      } catch {
        return c.substring(nameEQ.length, c.length);
      }
    }
  }
  return null;
};

/** Bearer headers for non-apiClient calls (propertyStatus, tabtrips_S, etc.) */
const authHeaders = (extra = {}) => {
  const token = getCookie('adminToken');
  return {
    ...extra,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const clearAuthAndRedirectLogin = () => {
  document.cookie = 'adminToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  document.cookie = 'adminUser=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  try {
    localStorage.removeItem('adminUser');
  } catch (_) {
    /* ignore */
  }
  window.location.href = '/login';
};

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include Authorization header
apiClient.interceptors.request.use(
  (config) => {
    const token = getCookie('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 401 → logout. 403 → reject with required_feature (do not logout / retry).
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const msg = (error.response?.data?.message || '').toLowerCase();
    if (
      status === 401 ||
      msg.includes('invalid token') ||
      msg.includes('token expired')
    ) {
      clearAuthAndRedirectLogin();
    } else if (status === 403) {
      error.required_feature = error.response?.data?.required_feature;
      error.forbiddenMessage = error.response?.data?.required_feature
        ? `No access (required: ${error.response.data.required_feature})`
        : error.response?.data?.message || 'No access';
    }
    return Promise.reject(error);
  }
);

// API Handler Functions for AdminPanelUserAssign

/**
 * Get all hotels and hosts data
 * @returns {Promise} API response with hotels and hosts data
 */
export const getHotelAndHost = async () => {
  try {
    const response = await apiClient.get('/getHotelAndHost');
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error fetching hotels and hosts:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to fetch hotels and hosts'
    };
  }
};

/**
 * Update hotel status (activate/deactivate)
 * @param {number} hotelId - Hotel ID
 * @param {number} hostId - Host ID
 * @param {string} status - Status ('active' or 'inactive')
 * @returns {Promise} API response
 */
export const updateHotelStatus = async (hotelId, hostId, status) => {
  try {
    const payload = {
      hotel_id: hotelId.toString(),
      host_id: hostId,
      status: status
    };

    const response = await apiClient.post('/updateStatus', payload);
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error updating hotel status:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to update hotel status'
    };
  }
};

/**
 * Assign existing user to hotel
 * @param {number} hotelId - Hotel ID
 * @param {string} email - User email
 * @returns {Promise} API response
 */
export const assignExistingUser = async (hotelId, email) => {
  try {
    const payload = {
      hotel_id: hotelId.toString(),
      email: email
    };

    const response = await apiClient.post('/assignExistedUser', payload);
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error assigning existing user:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to assign existing user'
    };
  }
};

/**
 * Create and assign new user to hotel
 * @param {number} hotelId - Hotel ID
 * @param {string} name - User name
 * @param {string} email - User email
 * @param {string} phone - User phone
 * @param {string} password - User password
 * @param {string} confirmPassword - Confirm password
 * @returns {Promise} API response
 */
export const assignNewUser = async (hotelId, name, email, phone, password, confirmPassword) => {
  try {
    // Validate password length
    if (password.length < 6) {
      return {
        success: false,
        error: 'Password must be at least 6 characters long'
      };
    }

    // Validate password confirmation
    if (password !== confirmPassword) {
      return {
        success: false,
        error: 'Passwords do not match'
      };
    }

    const payload = {
      hotel_id: hotelId,
      name: name,
      email: email,
      phone: phone,
      password: password,
      confirm_password: confirmPassword
    };

    // testApi.php — shared with extranet; no RBAC headers (call as before)
    const response = await axios.post('https://api.tabtrips.com/olivrweb/hotelapi/testApi.php/assignNewHost', payload, {
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error creating and assigning new user:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to create and assign new user'
    };
  }
};

/**
 * Search users by email or host_id for suggestions
 * @param {Array} allHosts - All hosts data from getHotelAndHost
 * @param {string} searchTerm - Search term (email or host_id)
 * @returns {Array} Filtered users matching search term
 */
export const searchUsers = (allHosts, searchTerm) => {
  if (!searchTerm || searchTerm.length < 2) {
    return [];
  }

  const lowerSearchTerm = searchTerm.toLowerCase();
  
  // Create unique users map to avoid duplicates
  const uniqueUsers = new Map();
  
  allHosts.forEach(host => {
    if (host.email && host.host_id) {
      const key = `${host.email}_${host.host_id}`;
      if (!uniqueUsers.has(key)) {
        uniqueUsers.set(key, {
          host_id: host.host_id,
          email: host.email,
          phone: host.phone
        });
      }
    }
  });

  // Filter users based on search term
  return Array.from(uniqueUsers.values()).filter(user => 
    user.email.toLowerCase().includes(lowerSearchTerm) || 
    user.host_id.toString().includes(lowerSearchTerm)
  );
};

/**
 * Get prepaid booking data
 * @param {string} month - Month in YYYY-MM format (optional)
 * @param {string} startDate - Start date in YYYY-MM-DD format (optional)
 * @param {string} endDate - End date in YYYY-MM-DD format (optional)
 * @returns {Promise} API response with prepaid booking data
 */
export const getPrePaidBooking = async (month = null, startDate = null, endDate = null) => {
  try {
    const payload = {};
    
    // Add date parameters if provided
    if (month) payload.month = month;
    if (startDate) payload.start_date = startDate;
    if (endDate) payload.end_date = endDate;
    
    const response = await apiClient.post('/getPrePaidBooking', payload);
    
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error fetching prepaid booking data:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to fetch prepaid booking data'
    };
  }
};

/**
 * Get postpaid booking data
 * @param {string} month - Month in YYYY-MM format (optional)
 * @param {string} startDate - Start date in YYYY-MM-DD format (optional)
 * @param {string} endDate - End date in YYYY-MM-DD format (optional)
 * @returns {Promise} API response with postpaid booking data
 */
export const getPostPaidBooking = async (month = null, startDate = null, endDate = null) => {
  try {
    const payload = {};
    
    // Add date parameters if provided
    if (month) payload.month = month;
    if (startDate) payload.start_date = startDate;
    if (endDate) payload.end_date = endDate;
    
    const response = await apiClient.post('/getPostPaidBooking', payload);
    
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error fetching postpaid booking data:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to fetch postpaid booking data'
    };
  }
};

/**
 * Set hotel payment mode
 * @param {number} hotelId - Hotel ID
 * @param {number} paymentMode - Payment mode (0 for both prepaid and postpaid, 1 for prepaid only)
 * @returns {Promise} API response
 */
export const setHotelPaymentMode = async (hotelId, paymentMode) => {
  try {
    const payload = {
      hotel_id: hotelId.toString(),
      payment_mode: paymentMode
    };

    const response = await apiClient.post('/setHotelPaymentMode', payload);
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error setting hotel payment mode:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to set hotel payment mode'
    };
  }
};

/**
 * Set hotel prepaid discount
 * @param {number|string} hotelId - Hotel ID
 * @param {number} value - Discount value
 * @param {string} type - Discount type ('percent' or 'mrp')
 * @returns {Promise} API response
 */
export const setHotelPrepaidDiscount = async (hotelId, value, type) => {
  try {
    const payload = {
      hotel_id: hotelId.toString(),
      value: Number(value),
      type: type
    };

    const response = await apiClient.post('/setHotelPrepaidDiscount', payload);
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error setting hotel prepaid discount:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to set hotel prepaid discount'
    };
  }
};

/**
 * Get specific hotel bookings with date filtering
 * @param {string} hotelId - Hotel ID
 * @param {string} month - Month in YYYY-MM format
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 * @param {string} status - Booking status ('prePaid' or 'postPaid')
 * @returns {Promise} API response with booking data
 */
export const getSpecificHotelBooking = async (hotelId, month, startDate, endDate, status) => {
  try {
    const payload = {
      hotel_id: hotelId.toString(),
      month: month,
      start_date: startDate,
      end_date: endDate,
      status: status
    };

    const response = await apiClient.post('/getSpecificHotelBooking', payload);
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error fetching specific hotel bookings:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to fetch specific hotel bookings'
    };
  }
};

/**
 * Update prepaid payment status for multiple bookings
 * @param {Array} bookingIds - Array of booking IDs
 * @param {string} status - Payment status ('Paid')
 * @returns {Promise} API response
 */
export const updatePrepaidPaymentStatus = async (bookingIds, status) => {
  try {
    const payload = {
      booking_ids: bookingIds,
      status: status
    };

    const response = await apiClient.post('/updatePrepaidPaymentStatus', payload);
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error updating prepaid payment status:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to update prepaid payment status'
    };
  }
};

/**
 * Set booking type for multiple bookings (used for removing postpaid bookings)
 * @param {Array} bookingIds - Array of booking IDs
 * @param {string} status - Booking status ('test' for removal)
 * @returns {Promise} API response
 */
export const setBookingType = async (bookingIds, status) => {
  try {
    const payload = {
      booking_ids: bookingIds,
      status: status
    };

    const response = await apiClient.post('/setBookingType', payload);
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error setting booking type:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to set booking type'
    };
  }
};

/**
 * Get all discounts for all hotels
 * @returns {Promise} API response with discounts data
 */
export const getAllDiscounts = async () => {
  try {
    const response = await apiClient.get('/getAllDiscounts');
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error fetching discounts:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to fetch discounts'
    };
  }
};

/**
 * Get hotels and hosts data with authorization
 * @returns {Promise} API response with hotels and hosts data
 */
export const getHotelAndHostWithAuth = async () => {
  try {
    const response = await axios.get('https://api.tabtrips.com/olivrweb/hotelapi/adminPanel.php/getHotelAndHost', {
      headers: {
        'Authorization': 'Bearer eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJpZCI6MiwiZW1haWwiOiIxMTEzMTMxYUBnbWFpbC5jb20iLCJpYXQiOjE3NTgxOTQ3OTgsImV4cCI6MTc1ODc5OTU5OH0.wNvrPWQ_CRpOLHtSCfQ1U0lQS99H6g1AEMgo0r80K-c',
        'Content-Type': 'application/json'
      },
      timeout: 10000
    });
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error fetching hotels and hosts with auth:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to fetch hotels and hosts'
    };
  }
};

/**
 * Add new room type/category
 * @param {string} roomName - Name of the room type
 * @returns {Promise} API response
 */
export const addNewRoomCategory = async (roomName) => {
  try {
    const payload = {
      roomName: roomName
    };

    const response = await apiClient.post('/setNewRoomCategory', payload);
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error adding new room category:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to add new room category'
    };
  }
};

/**
 * Search room categories by name keywords (adminPanel.php?action=searchRoomCategories)
 * @param {string} query - Search text e.g. "deluxe triple"
 */
export const searchRoomCategories = async (query) => {
  try {
    const q = (query || '').trim();
    if (!q) {
      return { success: true, data: [], count: 0, keywords: [] };
    }
    const response = await apiClient.post('', { q }, {
      params: { action: 'searchRoomCategories' },
      timeout: 30000,
    });
    const d = response.data;
    if (d && (d.status === false || d.status === 'false')) {
      return {
        success: false,
        error: d.message || 'Failed to search room categories',
        data: [],
        count: 0,
        raw: d,
      };
    }
    const list = Array.isArray(d?.data) ? d.data : [];
    return {
      success: true,
      data: list,
      count: d?.count ?? list.length,
      keywords: d?.keywords ?? [],
      raw: d,
    };
  } catch (error) {
    console.error('Error searching room categories:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to search room categories',
      data: [],
      count: 0,
      raw: error.response?.data,
    };
  }
};

/**
 * Hotels using a room category (adminPanel.php?action=getHotelsUsingRoomCategory)
 * @param {number|string} categoryId
 */
export const getHotelsUsingRoomCategory = async (categoryId) => {
  try {
    const response = await apiClient.post('', { category_id: Number(categoryId) }, {
      params: { action: 'getHotelsUsingRoomCategory' },
      timeout: 30000,
    });
    const d = response.data;
    if (d && (d.status === false || d.status === 'false')) {
      return {
        success: false,
        error: d.message || 'Failed to load hotels using category',
        hotels: [],
        raw: d,
      };
    }
    const hotels = Array.isArray(d?.hotels) ? d.hotels : Array.isArray(d?.data) ? d.data : [];
    return {
      success: true,
      hotels,
      count: d?.hotels_using_count ?? d?.count ?? hotels.length,
      category: d?.category,
      raw: d,
    };
  } catch (error) {
    console.error('Error loading hotels using room category:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to load hotels using category',
      hotels: [],
      raw: error.response?.data,
    };
  }
};

/**
 * Delete room type/category (adminPanel.php?action=deleteRoomCategory)
 * @param {number|string|{ category_id?: number|string, categoryid?: number|string, force?: boolean, confirm?: boolean }} payload
 */
export const deleteRoomCategory = async (payload) => {
  try {
    let body;
    if (payload != null && typeof payload === 'object') {
      const categoryId = payload.category_id ?? payload.categoryid;
      body = { category_id: Number(categoryId) };
      if (payload.force === true && payload.confirm === true) {
        body.force = true;
        body.confirm = true;
      }
    } else {
      body = { category_id: Number(payload) };
    }

    const response = await apiClient.post('', body, {
      params: { action: 'deleteRoomCategory' },
      timeout: 60000,
    });
    const d = response.data;
    if (d && (d.status === false || d.status === 'false')) {
      return {
        success: false,
        error: d.message || 'Failed to delete room category',
        code: d.code,
        inUse: d.code === 'IN_USE',
        partialFailure: d.code === 'PARTIAL_FAILURE',
        requiresForceConfirm: d.requires_force_confirm === true,
        data: d,
      };
    }
    return {
      success: true,
      message: d?.message || 'Room category deleted.',
      data: d,
    };
  } catch (error) {
    console.error('Error deleting room category:', error);
    const errData = error.response?.data;
    return {
      success: false,
      error: errData?.message || error.message || 'Failed to delete room category',
      code: errData?.code,
      inUse: errData?.code === 'IN_USE',
      partialFailure: errData?.code === 'PARTIAL_FAILURE',
      requiresForceConfirm: errData?.requires_force_confirm === true,
      data: errData,
    };
  }
};

/**
 * Get room types for a hotel (admin) – for delete room type UI
 * @param {number|string} hotelId - TabTrips hotel ID
 * @returns {Promise} API response with data.room_types array
 */
export const getRoomTypesForHotelAdmin = async (hotelId) => {
  try {
    const response = await apiClient.get('', {
      params: { action: 'getRoomTypesForHotelAdmin', hotel_id: String(hotelId) }
    });
    if (response.data && response.data.status === false) {
      return {
        success: false,
        error: response.data.message || 'Failed to fetch room types',
        data: response.data
      };
    }
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error fetching room types for hotel:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to fetch room types',
      data: error.response?.data
    };
  }
};

/**
 * Delete a room type for a hotel (admin)
 * @param {number|string} hotelId - TabTrips hotel ID
 * @param {number|string} roomtypeId - Room type ID to delete
 * @returns {Promise} API response
 */
export const deleteRoomTypeAdmin = async (hotelId, roomtypeId) => {
  try {
    const payload = {
      hotel_id: Number(hotelId),
      roomtype_id: Number(roomtypeId)
    };
    const response = await apiClient.post('', payload, {
      params: { action: 'deleteRoomTypeAdmin' }
    });
    if (response.data && response.data.status === false) {
      return {
        success: false,
        error: response.data.message || 'Failed to delete room type',
        data: response.data
      };
    }
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error deleting room type:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to delete room type',
      data: error.response?.data
    };
  }
};

const buildCancelPayload = (booking_id, hotel_id) => {
  const payload = { booking_id: String(booking_id) };
  if (hotel_id != null && String(hotel_id).trim() !== '') {
    const n = Number(hotel_id);
    payload.hotel_id = Number.isNaN(n) ? hotel_id : n;
  }
  return payload;
};

const wrapCancelResponse = (response, fallbackError) => {
  if (response.data && response.data.status === false) {
    return {
      success: false,
      error: response.data.message || fallbackError,
      data: response.data,
    };
  }
  return {
    success: true,
    data: response.data,
    message: response.data?.message,
  };
};

/**
 * Guest-side cancel + normal refund
 * adminPanel.php?action=cancelBooking
 */
export const cancelBooking = async (booking_id, hotel_id) => {
  try {
    const response = await apiClient.post('', buildCancelPayload(booking_id, hotel_id), {
      params: { action: 'cancelBooking' },
    });
    return wrapCancelResponse(response, 'Failed to cancel booking');
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to cancel booking',
      data: error.response?.data,
    };
  }
};

/** Alias — same as cancelBooking (guest side + normal refund) */
export const cancelBookingUserSide = async (booking_id, hotel_id) =>
  cancelBooking(booking_id, hotel_id);

/**
 * Hotel-side cancel + instant full refund
 * adminPanel.php?action=cancelBookingInstantRefund
 */
export const cancelBookingInstantRefund = async (booking_id, hotel_id) => {
  try {
    const response = await apiClient.post('', buildCancelPayload(booking_id, hotel_id), {
      params: { action: 'cancelBookingInstantRefund' },
    });
    return wrapCancelResponse(response, 'Failed to cancel booking with instant refund');
  } catch (error) {
    return {
      success: false,
      error:
        error.response?.data?.message ||
        error.message ||
        'Failed to cancel booking with instant refund',
      data: error.response?.data,
    };
  }
};

/**
 * Hotel-side cancel + normal refund (5–7 days)
 * adminPanel.php?action=cancelBookingHotelNormalRefund
 */
export const cancelBookingHotelNormalRefund = async (booking_id, hotel_id) => {
  try {
    const response = await apiClient.post('', buildCancelPayload(booking_id, hotel_id), {
      params: { action: 'cancelBookingHotelNormalRefund' },
    });
    return wrapCancelResponse(response, 'Failed to cancel booking (hotel normal refund)');
  } catch (error) {
    return {
      success: false,
      error:
        error.response?.data?.message ||
        error.message ||
        'Failed to cancel booking (hotel normal refund)',
      data: error.response?.data,
    };
  }
};

/**
 * Send cancel warning to hotel (booking_id only)
 * @param {string} booking_id - Booking ID (alphanumeric format like TT63459540)
 * @returns {Promise} API response
 */
export const sendCancelWarningToHotel = async (booking_id) => {
  try {
    const payload = { booking_id: booking_id.toString() };
    const response = await apiClient.post('/sendCancelWarningToHotel', payload);
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error sending cancel warning to hotel:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to send cancel warning to hotel'
    };
  }
};

/**
 * Set compensation credit note for a hotel
 * @param {string|number} hotelId - Hotel ID
 * @param {string} month - Month in YYYY-MM format
 * @param {string|number} amount - Compensation amount
 * @returns {Promise} API response
 */
export const setCompensationCreditNote = async (hotelId, month, amount) => {
  try {
    const payload = {
      hotel_id: hotelId.toString(),
      month: month,
      amount: Number(amount)
    };

    const response = await apiClient.post('/setCompensationCreditNote', payload);
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error setting compensation credit note:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to set compensation credit note'
    };
  }
};

/**
 * Update linked account for a hotel
 * @param {string|number} hotelId - Hotel ID
 * @returns {Promise} API response
 */
export const updateLinkedAccount = async (hotelId) => {
  try {
    const payload = {
      hotel_id: hotelId.toString()
    };

    const response = await apiClient.post('/updateLinkedAccount', payload);
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error updating linked account:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to update linked account'
    };
  }
};

/**
 * Get all hotels account status
 * @returns {Promise} API response with hotels account status data
 */
export const getAllHotelsAccountStatus = async () => {
  try {
    const response = await apiClient.get('/allHotelsAccountStatus'); 
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error fetching hotels account status:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to fetch hotels account status'
    };
  }
};

/**
 * Map TabTrips with StayFlexi
 * @param {string|number} tabtripsId - TabTrips Hotel ID
 * @param {string|number} stayFlexiId - StayFlexi Hotel ID
 * @param {string} isUseStayflexi - Whether to use StayFlexi ("yes" or "no")
 * @returns {Promise} API response
 */
export const mapTabtripsWithStayflexi = async (tabtripsId, stayFlexiId, isUseStayflexi) => {
  try {
    const payload = {
      tabtripsId: tabtripsId.toString(),
      stayFlexiId: stayFlexiId.toString(),
      isUseStayflexi: isUseStayflexi
    };

    const response = await apiClient.post('/mapTabtripsWithStayflexi', payload);
    
    // Check if the API returned status: false in the response body
    if (response.data && response.data.status === false) {
      // Handle both 'message' and 'messgae' (typo) fields
      const errorMessage = response.data.message || response.data.messgae || 'Failed to map TabTrips with StayFlexi';
      return {
        success: false,
        error: errorMessage,
        data: response.data
      };
    }
    
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error mapping TabTrips with StayFlexi:', error);
    // Handle both 'message' and 'messgae' (typo) fields in error response
    const errorMessage = error.response?.data?.message || error.response?.data?.messgae || error.message || 'Failed to map TabTrips with StayFlexi';
    return {
      success: false,
      error: errorMessage,
      data: error.response?.data
    };
  }
};

/**
 * Get hotel accounts
 * @returns {Promise} API response with hotel accounts data
 */
export const getHotelAccounts = async () => {
  try {
    const token = getCookie('adminToken');
    const response = await axios.get('https://api.tabtrips.com/olivrweb/hotelapi/tabtrips_S.php', {
      params: {
        action: 'getHotelAccounts'
      },
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      timeout: 10000
    });
    
    // Check if the API returned status: false in the response body
    if (response.data && response.data.status === false) {
      return {
        success: false,
        error: response.data.message || 'Failed to fetch hotel accounts',
        data: response.data
      };
    }
    
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error fetching hotel accounts:', error);
    // Handle both 'message' and 'messgae' (typo) fields in error response
    const errorMessage = error.response?.data?.message || error.response?.data?.messgae || error.message || 'Failed to fetch hotel accounts';
    return {
      success: false,
      error: errorMessage,
      data: error.response?.data
    };
  }
};

/**
 * Get payment settlements
 * @returns {Promise} API response with payment settlements data
 */
/**
 * Fetch payment settlements with optional filters.
 * @param {Object} [filters]
 * @param {string} [filters.from_date] YYYY-MM-DD
 * @param {string} [filters.to_date] YYYY-MM-DD
 * @param {string|number} [filters.month] YYYY-MM or 1–12
 * @param {string|number} [filters.year] required when month is 1–12
 * @param {boolean|number|string} [filters.exclude_cancelled]
 * @param {boolean|number|string} [filters.exclude_test_hotels] API default excludes; send 0 to include test hotels
 * @param {boolean|number|string} [filters.include_past_6_months] list loads default off
 */
export const getPaymentSettlements = async (filters = {}) => {
  try {
    const token = getCookie('adminToken');
    const params = { action: 'getPaymentSettlements' };

    if (filters.from_date && filters.to_date) {
      params.from_date = filters.from_date;
      params.to_date = filters.to_date;
    } else if (filters.month != null && filters.month !== '') {
      params.month = filters.month;
      if (filters.year != null && filters.year !== '') {
        params.year = filters.year;
      }
    }

    if (filters.exclude_cancelled === true || filters.exclude_cancelled === 1 || filters.exclude_cancelled === '1') {
      params.exclude_cancelled = 1;
    }

    // API defaults to excluding test hotels; only force-include when explicitly 0/false
    if (filters.exclude_test_hotels === false || filters.exclude_test_hotels === 0 || filters.exclude_test_hotels === '0') {
      params.exclude_test_hotels = 0;
    } else if (filters.exclude_test_hotels === true || filters.exclude_test_hotels === 1 || filters.exclude_test_hotels === '1') {
      params.exclude_test_hotels = 1;
    }

    // Default off — past-6m row arrays make the payload huge; opt in only for drill-down.
    if (filters.include_past_6_months === true || filters.include_past_6_months === 1 || filters.include_past_6_months === '1') {
      params.include_past_6_months = 1;
    } else {
      params.include_past_6_months = 0;
    }

    const response = await axios.get('https://api.tabtrips.com/olivrweb/hotelapi/tabtrips_S.php', {
      params,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      timeout: 60000
    });
    
    // Check if the API returned status: false in the response body
    if (response.data && response.data.status === false) {
      return {
        success: false,
        error: response.data.message || 'Failed to fetch payment settlements',
        data: response.data
      };
    }
    
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error fetching payment settlements:', error);
    // Handle both 'message' and 'messgae' (typo) fields in error response
    const errorMessage = error.response?.data?.message || error.response?.data?.messgae || error.message || 'Failed to fetch payment settlements';
    return {
      success: false,
      error: errorMessage,
      data: error.response?.data
    };
  }
};

/**
 * Process payment settlement
 * @param {number|string} bookingId - Booking ID to process settlement for
 * @returns {Promise} API response with settlement processing result
 */
export const processSettlement = async (bookingId) => {
  try {
    const token = getCookie('adminToken');
    const response = await axios.post(
      'https://api.tabtrips.com/olivrweb/hotelapi/tabtrips_S.php?action=processSettlement',
      {
        booking_id: bookingId
      },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 30000 // 30 seconds timeout for Razorpay API calls
      }
    );
    
    // Check if the API returned status: false in the response body
    if (response.data && response.data.status === false) {
      return {
        success: false,
        error: response.data.message || 'Failed to process settlement',
        data: response.data
      };
    }
    
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error processing settlement:', error);
    // Handle both 'message' and 'messgae' (typo) fields in error response
    const errorMessage = error.response?.data?.message || error.response?.data?.messgae || error.message || 'Failed to process settlement';
    return {
      success: false,
      error: errorMessage,
      data: error.response?.data
    };
  }
};

/**
 * Get all hotels bank details
 * @returns {Promise} API response with hotel bank details data
 */
export const getAllHotelsBankDetails = async () => {
  try {
    const token = getCookie('adminToken');
    const response = await axios.get('https://api.tabtrips.com/olivrweb/hotelapi/tabtrips_S.php', {
      params: {
        action: 'getAllHotelsBankDetails'
      },
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      timeout: 10000
    });
    
    // Check if the API returned status: false in the response body
    if (response.data && response.data.status === false) {
      return {
        success: false,
        error: response.data.message || 'Failed to fetch hotel bank details',
        data: response.data
      };
    }
    
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error fetching hotel bank details:', error);
    // Handle both 'message' and 'messgae' (typo) fields in error response
    const errorMessage = error.response?.data?.message || error.response?.data?.messgae || error.message || 'Failed to fetch hotel bank details';
    return {
      success: false,
      error: errorMessage,
      data: error.response?.data
    };
  }
};

/**
 * Generate voucher (guest or hotelier)
 * @param {number|string} bookingId - Booking ID to generate voucher for
 * @param {string} voucherType - Type of voucher ('guest' or 'hotelier')
 * @returns {Promise} API response with PDF base64 data
 */
export const generateVoucher = async (bookingId, voucherType) => {
  try {
    const token = getCookie('adminToken');
    const response = await axios.get('https://api.tabtrips.com/olivrweb/hotelapi/tabtrips_S.php', {
      params: {
        action: 'generateVoucher',
        booking_id: bookingId.toString(),
        voucher_type: voucherType
      },
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      timeout: 30000 // 30 seconds timeout for PDF generation
    });
    
    // Check if the API returned status: false in the response body
    if (response.data && response.data.status === false) {
      return {
        success: false,
        error: response.data.message || 'Failed to generate voucher',
        data: response.data
      };
    }
    
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error generating voucher:', error);
    // Handle both 'message' and 'messgae' (typo) fields in error response
    const errorMessage = error.response?.data?.message || error.response?.data?.messgae || error.message || 'Failed to generate voucher';
    return {
      success: false,
      error: errorMessage,
      data: error.response?.data
    };
  }
};

/**
 * Mark payment as settled
 * @param {number|string} bookingId - Booking ID
 * @param {string} transferId - Transfer ID
 * @returns {Promise} API response with settlement marking result
 */
export const markPaymentAsSettled = async (bookingId, transferId) => {
  try {
    const token = getCookie('adminToken');
    const response = await axios.post(
      'https://api.tabtrips.com/olivrweb/hotelapi/tabtrips_S.php?action=markPaymentAsSettled',
      {
        booking_id: bookingId,
        transfer_id: transferId
      },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 30000 // 30 seconds timeout
      }
    );
    
    // Check if the API returned status: false in the response body
    if (response.data && response.data.status === false) {
      return {
        success: false,
        error: response.data.message || 'Failed to mark payment as settled',
        data: response.data
      };
    }
    
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error marking payment as settled:', error);
    // Handle both 'message' and 'messgae' (typo) fields in error response
    const errorMessage = error.response?.data?.message || error.response?.data?.messgae || error.message || 'Failed to mark payment as settled';
    return {
      success: false,
      error: errorMessage,
      data: error.response?.data
    };
  }
};

/**
 * Mark invoice as settled for a hotel for a given month
 * @param {number} hotelId - Hotel ID
 * @param {string} month - Month in YYYY-MM format (e.g. "2026-02")
 * @returns {Promise} API response
 */
export const markInvoiceAsSettled = async (hotelId, month) => {
  try {
    const token = getCookie('adminToken');
    const response = await axios.post(
      'https://api.tabtrips.com/olivrweb/hotelapi/tabtrips_S.php?action=markInvoiceAsSettled',
      { hotel_id: Number(hotelId), month },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 15000
      }
    );
    if (response.data && response.data.status === false) {
      return {
        success: false,
        error: response.data.message || 'Failed to mark invoice as settled',
        data: response.data
      };
    }
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error marking invoice as settled:', error);
    const errorMessage = error.response?.data?.message || error.response?.data?.messgae || error.message || 'Failed to mark invoice as settled';
    return { success: false, error: errorMessage, data: error.response?.data };
  }
};

// ==================== PROMO CODES APIs ====================
// All use adminPanel.php?action=<action> with Bearer token

/**
 * Get all promo codes
 * @returns {Promise} API response with data array of promo codes
 */
export const getPromoCodes = async () => {
  try {
    const response = await apiClient.get('', { params: { action: 'getPromoCodes' } });
    if (response.data && response.data.status === false) {
      return { success: false, error: response.data.message || 'Failed to fetch promo codes', data: response.data };
    }
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error fetching promo codes:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to fetch promo codes',
      data: error.response?.data
    };
  }
};

/**
 * Add new promo code
 * @param {Object} payload - { code, type, value, discount_borne_by, ...optional }
 * @returns {Promise} API response
 */
export const addPromoCode = async (payload) => {
  try {
    const response = await apiClient.post('', payload, { params: { action: 'addPromoCode' } });
    if (response.data && response.data.status === false) {
      return { success: false, error: response.data.message || 'Failed to add promo code', data: response.data };
    }
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error adding promo code:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to add promo code',
      data: error.response?.data
    };
  }
};

/**
 * Update existing promo code (partial update)
 * @param {Object} payload - { id (required), ...fields to update }
 * @returns {Promise} API response
 */
export const updatePromoCode = async (payload) => {
  try {
    const response = await apiClient.post('', payload, { params: { action: 'updatePromoCode' } });
    if (response.data && response.data.status === false) {
      return { success: false, error: response.data.message || 'Failed to update promo code', data: response.data };
    }
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error updating promo code:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to update promo code',
      data: error.response?.data
    };
  }
};

/**
 * Delete promo code (only if usage_count === 0)
 * @param {number} id - Promo code id
 * @returns {Promise} API response
 */
export const deletePromoCode = async (id) => {
  try {
    const response = await apiClient.post('', { id }, { params: { action: 'deletePromoCode' } });
    if (response.data && response.data.status === false) {
      return { success: false, error: response.data.message || 'Failed to delete promo code', data: response.data };
    }
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error deleting promo code:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to delete promo code',
      data: error.response?.data
    };
  }
};

const adminPanelApi = {
  getHotelAndHost,
  updateHotelStatus,
  assignExistingUser,
  assignNewUser,
  searchUsers,
  getPrePaidBooking,
  getPostPaidBooking,
  setHotelPaymentMode,
  setHotelPrepaidDiscount,
  getSpecificHotelBooking,
  updatePrepaidPaymentStatus,
  setBookingType,
  getAllDiscounts,
  getHotelAndHostWithAuth
};

// ==================== GOOGLE / PROPERTY STATUS (propertyStatus.php) ====================
const PROPERTY_STATUS_BASE = 'https://api.tabtrips.com/olivrweb/hotelapi/propertyStatus.php';

/**
 * Get all hotels with Google live status (propertyStatus.php).
 * API should return data[] with id, hotel_id, hotel_name, stayflexi_hotel_id, google_live_status, location_point (optional).
 * @returns {Promise} { success, count, data: [{ id, hotel_id, hotel_name, stayflexi_hotel_id, google_live_status, location_point? }] }
 */
export const getHotelsPropertyStatus = async () => {
  try {
    const response = await axios.get(PROPERTY_STATUS_BASE, {
      params: { action: 'getHotels' },
      timeout: 15000,
      headers: authHeaders({ 'Content-Type': 'application/json' }),
    });
    const ok = response.data && (response.data.success === true || response.data.data !== undefined);
    if (!ok) {
      return {
        success: false,
        error: response.data?.message || 'Failed to fetch hotels',
        data: response.data,
        count: 0
      };
    }
    return {
      success: true,
      count: response.data.count ?? (response.data.data?.length ?? 0),
      data: response.data.data || []
    };
  } catch (error) {
    console.error('Error fetching property status:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to fetch hotels',
      data: error.response?.data,
      count: 0
    };
  }
};

/**
 * Update Google live status for a hotel (propertyStatus.php)
 * @param {string|number} id - Hotel id (e.g. 50050001)
 * @param {string} status - 'live' | 'pending' | 'not_live' | 'no_gha'
 * @returns {Promise} API response
 */
export const updateGoogleLiveStatus = async (id, status) => {
  try {
    const response = await axios.post(
      PROPERTY_STATUS_BASE,
      new URLSearchParams({ action: 'updateGoogleLive', id: String(id), status: String(status) }),
      {
        timeout: 10000,
        headers: authHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }),
      }
    );
    if (response.data && response.data.success === false) {
      return {
        success: false,
        error: response.data.message || 'Failed to update status',
        data: response.data
      };
    }
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error updating Google live status:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to update status',
      data: error.response?.data
    };
  }
};

/**
 * Update location point for a hotel (propertyStatus.php).
 * Curl: curl -X POST "https://api.tabtrips.com/olivrweb/hotelapi/propertyStatus.php" \
 *   -d "action=updateLocationPoint&id=50050001&location_point=28.6139,77.2090"
 * @param {string|number} id - Hotel id (e.g. 50050001)
 * @param {string} location_point - e.g. "lat,lng" or "lat,lng,radius" as returned by backend
 * @returns {Promise} API response
 */
export const updatePropertyLocationPoint = async (id, location_point) => {
  try {
    const response = await axios.post(
      PROPERTY_STATUS_BASE,
      new URLSearchParams({
        action: 'updateLocationPoint',
        id: String(id),
        location_point: String(location_point ?? '')
      }),
      {
        timeout: 10000,
        headers: authHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }),
      }
    );
    if (response.data && response.data.success === false) {
      return {
        success: false,
        error: response.data.message || 'Failed to update location',
        data: response.data
      };
    }
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to update location',
      data: error.response?.data
    };
  }
};

/**
 * Update Google Maps embed for a hotel (propertyStatus.php?action=updateLocationPoint)
 * @param {string|number} hotel_id - TabTrips hotel ID
 * @param {string} embed_map - Full iframe HTML or embed snippet
 */
export const updatePropertyEmbedMap = async (hotel_id, embed_map) => {
  try {
    const response = await axios.post(
      PROPERTY_STATUS_BASE,
      {
        hotel_id: Number(hotel_id),
        embed_map: String(embed_map ?? ''),
      },
      {
        params: { action: 'updateLocationPoint' },
        timeout: 15000,
        headers: authHeaders({ 'Content-Type': 'application/json' }),
      }
    );
    if (response.data && (response.data.success === false || response.data.status === false)) {
      return {
        success: false,
        error: response.data.message || 'Failed to update embed map',
        data: response.data,
      };
    }
    return { success: true, message: response.data?.message, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.forbiddenMessage || error.response?.data?.message || error.message || 'Failed to update embed map',
      data: error.response?.data,
      required_feature: error.required_feature || error.response?.data?.required_feature,
    };
  }
};

/**
 * Update AI pricing status for a hotel (propertyStatus.php)
 * Curl:
 *   curl -X POST "https://api.tabtrips.com/olivrweb/hotelapi/propertyStatus.php" \
 *     -d "action=updateAiPricing&id=123&status=active"
 * @param {string|number} id - Hotel id (e.g. 50050001)
 * @param {string} status - 'active' | 'inactive'
 * @returns {Promise} API response
 */
export const updateAiPricingStatus = async (id, status) => {
  try {
    const response = await axios.post(
      PROPERTY_STATUS_BASE,
      new URLSearchParams({
        action: 'updateAiPricing',
        id: String(id),
        status: String(status)
      }),
      {
        timeout: 10000,
        headers: authHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }),
      }
    );
    if (response.data && response.data.success === false) {
      return {
        success: false,
        error: response.data.message || 'Failed to update AI pricing',
        data: response.data
      };
    }
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to update AI pricing',
      data: error.response?.data
    };
  }
};

/**
 * Get mapping data by hotel ID (adminPanel.php?action=getMappingDataByHotelId&hotel_id=...)
 * @param {string|number} hotelId - TabTrips hotel ID
 * @returns {Promise<{ success: boolean, message?: string, hotel?: object, mapping?: array, room_types?: array, channel_manager?: object, error?: string }>}
 */
export const getMappingDataByHotelId = async (hotelId) => {
  try {
    const response = await apiClient.get('', {
      params: { action: 'getMappingDataByHotelId', hotel_id: String(hotelId) }
    });
    const d = response.data;
    if (d && d.status === false) {
      return {
        success: false,
        error: d.message || 'Failed to fetch mapping data',
        data: d
      };
    }
    return {
      success: true,
      message: d?.message,
      hotel: d?.hotel ?? null,
      mapping: Array.isArray(d?.mapping) ? d.mapping : [],
      room_types: Array.isArray(d?.room_types) ? d.room_types : [],
      channel_manager: d?.channel_manager ?? null,
      data: d
    };
  } catch (error) {
    console.error('Error fetching mapping data:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to fetch mapping data',
      data: error.response?.data
    };
  }
};

/**
 * Push hotel inventory/rates to Stayflexi (tabtrips_S.php?action=pushHotelToStayflexi&hotel_id=...)
 * @param {string|number} hotelId - TabTrips hotel ID
 * @returns {Promise<{ success: boolean, message?: string, status?: boolean, data?: any, error?: string }>}
 */
export const triggerRateSync = async (hotelId) => {
  try {
    const token = getCookie('adminToken');
    const response = await axios.get('https://api.tabtrips.com/olivrweb/hotelapi/tabtrips_S.php', {
      params: {
        action: 'pushHotelToStayflexi',
        hotel_id: String(hotelId),
      },
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      timeout: 30000,
    });
    const d = response.data;
    if (d && d.status === false) {
      return {
        success: false,
        status: false,
        message: d.message || 'Rate sync failed',
        error: d.message || 'Rate sync failed',
        data: d,
      };
    }
    return {
      success: true,
      status: d?.status,
      message: d?.message || 'Hotel inventory and rates pushed to Stayflexi',
      data: d,
    };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to push hotel to Stayflexi',
      data: error.response?.data,
    };
  }
};

const DASHBOARD_API_BASE = 'https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php';
const TABTRIPS_S_BASE = 'https://api.tabtrips.com/olivrweb/hotelapi/tabtrips_S.php';

/**
 * Regenerate sitemap (tabtrips_S.php?action=updateSitemap)
 * @returns {Promise<{ success: boolean, message?: string, data?: any, error?: string }>}
 */
export const updateSitemap = async () => {
  try {
    const token = getCookie('adminToken');
    const response = await axios.get(TABTRIPS_S_BASE, {
      params: { action: 'updateSitemap' },
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      timeout: 120000,
    });
    const d = response.data;
    if (d && d.status === false) {
      return {
        success: false,
        error: d.message || 'Failed to update sitemap',
        data: d,
      };
    }
    return {
      success: true,
      message: d?.message || 'Sitemap updated',
      data: d,
    };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to update sitemap',
      data: error.response?.data,
    };
  }
};

/**
 * Onboard hotel on Stayflexi (tabtrips_S.php?action=onboardStayflexiHotel&hotel_id=...)
 * @param {string|number} hotelId - TabTrips hotel ID
 */
export const onboardStayflexiHotel = async (hotelId) => {
  try {
    const token = getCookie('adminToken');
    const response = await axios.get(TABTRIPS_S_BASE, {
      params: {
        action: 'onboardStayflexiHotel',
        hotel_id: String(hotelId),
      },
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      timeout: 60000,
    });
    const d = response.data;
    if (d && d.status === false) {
      return {
        success: false,
        error: d.message || 'Failed to onboard hotel on Stayflexi',
        data: d,
      };
    }
    return {
      success: true,
      message: d?.message || 'Hotel onboarded on Stayflexi',
      data: d,
    };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to onboard hotel on Stayflexi',
      data: error.response?.data,
    };
  }
};

/**
 * Finance GST report — filter catalog (tabtrips_S.php?action=getFinanceFilterOptions)
 */
export const getFinanceFilterOptions = async () => {
  try {
    const token = getCookie('adminToken');
    const response = await axios.get(TABTRIPS_S_BASE, {
      params: { action: 'getFinanceFilterOptions' },
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      timeout: 30000,
    });
    const d = response.data;
    if (d && d.status === false) {
      return { success: false, error: d.message || 'Failed to load finance filters', data: d };
    }
    return { success: true, data: d };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to load finance filters',
      data: error.response?.data,
    };
  }
};

/**
 * Finance / GST report (tabtrips_S.php?action=getFinanceGstReport)
 * @param {object} payload - start_date, end_date, date_field, page, limit, filters, include
 */
export const getFinanceGstReport = async (payload) => {
  try {
    const token = getCookie('adminToken');
    const response = await axios.post(TABTRIPS_S_BASE, payload, {
      params: { action: 'getFinanceGstReport' },
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      timeout: 180000,
    });
    const d = response.data;
    if (d && d.status === false) {
      return { success: false, error: d.message || 'Failed to load finance report', data: d };
    }
    return { success: true, data: d };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to load finance report',
      data: error.response?.data,
    };
  }
};

/**
 * Update mapping StayFlexi Hotel ID (adminPanel.php?action=updateMappingStayflexiId)
 * @param {number|string} tabtrips_hotel_id
 * @param {number|string} stayflexi_hotel_id - current
 * @param {string} use_stayflexi - "yes" | "no"
 * @param {number|string} new_stayflexi_hotel_id
 */
export const updateMappingStayflexiId = async (tabtrips_hotel_id, stayflexi_hotel_id, use_stayflexi, new_stayflexi_hotel_id) => {
  try {
    const payload = {
      tabtrips_hotel_id: Number(tabtrips_hotel_id),
      stayflexi_hotel_id: Number(stayflexi_hotel_id),
      use_stayflexi: String(use_stayflexi),
      new_stayflexi_hotel_id: Number(new_stayflexi_hotel_id)
    };
    const response = await apiClient.post('', payload, { params: { action: 'updateMappingStayflexiId' } });
    const d = response.data;
    if (d && d.status === false) {
      return { success: false, error: d.message || 'Failed to update mapping', data: d };
    }
    return { success: true, message: d?.message, data: d };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to update mapping',
      data: error.response?.data
    };
  }
};

/**
 * Delete mapping row (adminPanel.php?action=deleteMappingRow)
 */
export const deleteMappingRow = async (tabtrips_hotel_id, stayflexi_hotel_id, use_stayflexi) => {
  try {
    const payload = {
      tabtrips_hotel_id: Number(tabtrips_hotel_id),
      stayflexi_hotel_id: Number(stayflexi_hotel_id),
      use_stayflexi: String(use_stayflexi)
    };
    const response = await apiClient.post('', payload, { params: { action: 'deleteMappingRow' } });
    const d = response.data;
    if (d && d.status === false) {
      return { success: false, error: d.message || 'Failed to delete mapping', data: d };
    }
    return { success: true, message: d?.message, data: d };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to delete mapping',
      data: error.response?.data
    };
  }
};

/**
 * Map room type and rate plan (adminPanel.php?action=mapRoomTypeAndRatePlan)
 * Send at least one of stayflexiRoomTypeId, stayflexiRatePlanId
 */
export const mapRoomTypeAndRatePlan = async (hotelId, roomtypeId, { stayflexiRoomTypeId, stayflexiRatePlanId }) => {
  try {
    const payload = {
      hotelId: Number(hotelId),
      roomtypeId: Number(roomtypeId)
    };
    if (stayflexiRoomTypeId != null) payload.stayflexiRoomTypeId = Number(stayflexiRoomTypeId);
    if (stayflexiRatePlanId != null) payload.stayflexiRatePlanId = Number(stayflexiRatePlanId);
    const response = await apiClient.post('', payload, { params: { action: 'mapRoomTypeAndRatePlan' } });
    const d = response.data;
    if (d && d.status === false) {
      return { success: false, error: d.message || 'Failed to update', data: d };
    }
    return { success: true, message: d?.message, data: d };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to update',
      data: error.response?.data
    };
  }
};

/**
 * Update single room type field (adminPanel.php?action=updateRoomTypeField)
 * One of: total_room_count, occupancy, isActive
 */
export const updateRoomTypeField = async (hotel_id, roomtype_id, field, value) => {
  try {
    const payload = {
      hotel_id: Number(hotel_id),
      roomtype_id: Number(roomtype_id),
      [field]: value
    };
    const response = await apiClient.post('', payload, { params: { action: 'updateRoomTypeField' } });
    const d = response.data;
    if (d && d.status === false) {
      return { success: false, error: d.message || 'Failed to update', data: d };
    }
    return { success: true, message: d?.message, data: d };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to update',
      data: error.response?.data
    };
  }
};

/**
 * Set hotel or room type amenities (adminPanel.php?action=setAmenities)
 * @param {Object} payload - { hotel_id, type: 'hotel'|'roomtype', amenities: string|string[], roomtype_id?: number, roomtype_ids?: number[] }
 */
export const setAmenities = async (payload) => {
  try {
    const body = {
      hotel_id: Number(payload.hotel_id),
      type: String(payload.type),
    };
    if (payload.type === 'roomtype') {
      const ids = Array.isArray(payload.roomtype_ids)
        ? payload.roomtype_ids
        : payload.roomtype_id != null
          ? [payload.roomtype_id]
          : [];
      body.roomtype_ids = ids.map((id) => Number(id));
      body.amenities = Array.isArray(payload.amenities)
        ? payload.amenities.map((a) => String(a).trim()).filter(Boolean)
        : String(payload.amenities ?? '')
            .split(',')
            .map((a) => a.trim())
            .filter(Boolean);
    } else {
      body.amenities = Array.isArray(payload.amenities)
        ? payload.amenities.map((a) => String(a).trim()).filter(Boolean).join(', ')
        : String(payload.amenities ?? '');
    }
    const response = await apiClient.post('', body, { params: { action: 'setAmenities' } });
    const d = response.data;
    if (d && d.status === false) {
      return { success: false, error: d.message || 'Failed to set amenities', data: d };
    }
    return { success: true, message: d?.message, data: d };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to set amenities',
      data: error.response?.data,
    };
  }
};

/**
 * Fetch StayFlexi room type mappings (tabtrips_S.php?action=fetchStayflexiRoomTypeMappings)
 */
export const fetchStayflexiRoomTypeMappings = async (hotel_id) => {
  try {
    const token = getCookie('adminToken');
    const response = await axios.get(TABTRIPS_S_BASE, {
      params: { action: 'fetchStayflexiRoomTypeMappings', hotel_id: String(hotel_id) },
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      timeout: 10000
    });
    const d = response.data;
    if (d && d.status === false) {
      return { success: false, error: d.message || 'Sync failed', data: d };
    }
    return { success: true, data: d };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Sync failed',
      data: error.response?.data
    };
  }
};

/**
 * Get available room types for name-change suggestions (dashboardApi.php?action=getAvailableRoomTypes)
 */
export const getAvailableRoomTypes = async () => {
  try {
    const token = getCookie('adminToken');
    const response = await axios.get(DASHBOARD_API_BASE, {
      params: { action: 'getAvailableRoomTypes' },
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      timeout: 10000
    });
    const d = response.data;
    if (d && d.status === false) {
      return { success: false, error: d.message || 'Failed to fetch', data: d };
    }
    const list = Array.isArray(d?.data) ? d.data : [];
    return { success: true, data: list };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to fetch room types',
      data: error.response?.data
    };
  }
};

/**
 * Change room type name/category (dashboardApi.php?action=changeRoomType)
 */
export const changeRoomType = async (hotel_id, previous_room_type_category_id, new_room_type_category_id) => {
  try {
    const token = getCookie('adminToken');
    const payload = {
      hotel_id: Number(hotel_id),
      previous_room_type_category_id: Number(previous_room_type_category_id),
      new_room_type_category_id: Number(new_room_type_category_id),
      token: token || ''
    };
    const response = await axios.post(DASHBOARD_API_BASE, payload, {
      params: { action: 'changeRoomType' },
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      timeout: 10000
    });
    const d = response.data;
    if (d && d.status === false) {
      return { success: false, error: d.message || 'Failed to change room type', data: d };
    }
    return { success: true, message: d?.message, data: d };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to change room type',
      data: error.response?.data
    };
  }
};

/**
 * Set AI pricing undercut value in INR (mapsPanelScrape.php?action=setMapsAiPricingUndercutInr)
 * @param {number|string} inr - Undercut amount in INR
 * @returns {Promise} API response
 */
export const setMapsAiPricingUndercutInr = async (inr) => {
  try {
    const response = await axios.post(
      'https://api.tabtrips.com/olivrweb/hotelapi/mapsPanelScrape.php?action=setMapsAiPricingUndercutInr&cron_key=TabtripsAiPricingCron2026',
      { inr: Number(inr) },
      {
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        timeout: 15000,
      }
    );
    const d = response.data;
    if (d && d.status === false) {
      return {
        success: false,
        error: d.message || 'Failed to set AI pricing discount',
        data: d
      };
    }
    return { success: true, data: d };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to set AI pricing discount',
      data: error.response?.data
    };
  }
};

/**
 * Set StayFlexi channel config token (adminPanel.php?action=setStayflexiChannelConfigToken&token=...)
 * @param {string} token - JWT token for StayFlexi channel config
 * @returns {Promise<{ success: boolean, data?: object, error?: string }>}
 */
export const setStayflexiChannelConfigToken = async (token) => {
  try {
    const response = await apiClient.get('', {
      params: {
        action: 'setStayflexiChannelConfigToken',
        token: String(token).trim(),
      },
      timeout: 20000,
    });
    const d = response.data;
    if (d && (d.status === false || d.status === 'false')) {
      return {
        success: false,
        error: d.message || 'Failed to set StayFlexi mapping token',
        data: d,
      };
    }
    return { success: true, data: d };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to set StayFlexi mapping token',
      data: error.response?.data,
    };
  }
};

/**
 * Trigger nearby places setup for a hotel (adminPanel.php?action=cronSetHotelNearByPlaces)
 * @param {number|string} hotelId - Hotel ID
 * @returns {Promise} API response
 */
export const cronSetHotelNearByPlaces = async (hotelId) => {
  try {
    const response = await apiClient.get('', {
      params: {
        action: 'cronSetHotelNearByPlaces',
        hotel_id: String(hotelId),
      },
      timeout: 20000,
    });
    const d = response.data;
    if (d && d.status === false) {
      return {
        success: false,
        error: d.message || 'Failed to set nearby places',
        data: d
      };
    }
    return { success: true, data: d };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to set nearby places',
      data: error.response?.data
    };
  }
};

/**
 * Add a manual hotel review (testApi.php?action=addManualHotelReview)
 * @param {object} payload - Review fields including optional insert_time (YYYY-MM-DD HH:mm:ss)
 * @returns {Promise<{ success: boolean, data?: object, error?: string }>}
 */
export const addManualHotelReview = async (payload) => {
  try {
    const response = await axios.post(
      'https://api.tabtrips.com/olivrweb/hotelapi/testApi.php',
      {
        ...payload,
        would_recommend: 1,
      },
      {
        params: {
          action: 'addManualHotelReview',
          repair_key: 'Tabtrips@1234',
        },
        timeout: 20000,
        // testApi.php — shared with extranet; no RBAC headers (call as before)
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    const d = response.data;
    if (d && d.status === false) {
      return {
        success: false,
        error: d.message || 'Failed to add review',
        data: d,
      };
    }
    return { success: true, data: d };
  } catch (error) {
    return {
      success: false,
      error:
        error.response?.data?.message ||
        (typeof error.response?.data === 'string' ? error.response.data : null) ||
        error.message ||
        'Failed to add review',
      data: error.response?.data,
    };
  }
};

/**
 * Support admin: booking lookup (adminPanel.php?action=getSupportBookingDetails)
 * POST JSON body: { booking_code: "TT…" } or { booking_id: 12345 }
 * Uses Bearer token via apiClient.
 * @param {{ booking_code?: string, booking_id?: number|string }} payload
 * @returns {Promise<{ success: boolean, guest_booking?: object, property_and_host?: object, error?: string, raw?: object }>}
 */
// ==================== ROOM TYPE ADD — APPROVAL QUEUE ====================

/**
 * List room type approval requests (adminPanel.php?action=listRoomTypeApprovalRequests)
 * @param {{ status?: string, hotel_id?: string|number, admin_marked_created?: 'yes'|'no'|'' }} filters
 * @returns {Promise<{ success: boolean, rows?: array, message?: string, error?: string, data?: object }>}
 */
export const listRoomTypeApprovalRequests = async (filters = {}) => {
  try {
    const params = { action: 'listRoomTypeApprovalRequests' };
    if (filters.status != null && String(filters.status).trim() !== '') {
      params.status = String(filters.status).trim();
    }
    if (filters.hotel_id != null && String(filters.hotel_id).trim() !== '') {
      params.hotel_id = String(filters.hotel_id).trim();
    }
    if (filters.admin_marked_created != null && String(filters.admin_marked_created).trim() !== '') {
      params.admin_marked_created = String(filters.admin_marked_created).trim().toLowerCase();
    }
    const response = await apiClient.get('', { params, timeout: 30000 });
    const d = response.data;
    if (d && (d.status === false || d.status === 'false')) {
      return {
        success: false,
        error: d.message || 'Failed to list approval requests',
        data: d,
        rows: [],
      };
    }
    const rows = Array.isArray(d?.data) ? d.data : [];
    return {
      success: true,
      rows,
      message: d?.message,
      data: d,
    };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to list approval requests',
      data: error.response?.data,
      rows: [],
    };
  }
};

/**
 * Legacy: close log when roomtype already exists (adminPanel.php?action=approveRoomTypeApprovalRequest).
 * Does not create rooms — only valid for pending/failed. Prefer Mark reviewed for approved direct-add logs.
 * @param {string|number} requestId
 */
export const approveRoomTypeApprovalRequest = async (requestId) => {
  try {
    const response = await apiClient.post(
      '',
      { request_id: Number(requestId) },
      { params: { action: 'approveRoomTypeApprovalRequest' } }
    );
    const d = response.data;
    if (d && (d.status === false || d.status === 'false')) {
      return { success: false, error: d.message || 'Could not close log', data: d };
    }
    return { success: true, data: d };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Could not close log',
      data: error.response?.data,
    };
  }
};

/**
 * Mark log reviewed / return to inbox (adminPanel.php?action=setRoomTypeRequestAdminMarkedCreated)
 * @param {string|number} requestId
 * @param {'yes'|'no'} adminMarkedCreated
 */
export const setRoomTypeRequestAdminMarkedCreated = async (requestId, adminMarkedCreated) => {
  try {
    const v = String(adminMarkedCreated || '').toLowerCase();
    const normalized = v === 'yes' || v === 'y' ? 'yes' : 'no';
    const response = await apiClient.post(
      '',
      { request_id: Number(requestId), admin_marked_created: normalized },
      { params: { action: 'setRoomTypeRequestAdminMarkedCreated' } }
    );
    const d = response.data;
    if (d && (d.status === false || d.status === 'false')) {
      return { success: false, error: d.message || 'Update failed', data: d };
    }
    return { success: true, data: d };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Update failed',
      data: error.response?.data,
    };
  }
};

/**
 * Reject a pending request (adminPanel.php?action=rejectRoomTypeApprovalRequest)
 * @param {string|number} requestId
 * @param {string} [reason]
 */
export const rejectRoomTypeApprovalRequest = async (requestId, reason = '') => {
  try {
    const body = { request_id: Number(requestId) };
    if (reason != null && String(reason).trim() !== '') {
      body.reason = String(reason).trim();
    }
    const response = await apiClient.post('', body, {
      params: { action: 'rejectRoomTypeApprovalRequest' },
    });
    const d = response.data;
    if (d && (d.status === false || d.status === 'false')) {
      return { success: false, error: d.message || 'Reject failed', data: d };
    }
    return { success: true, data: d };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Reject failed',
      data: error.response?.data,
    };
  }
};

/**
 * Reset failed request to pending (adminPanel.php?action=resetRoomTypeApprovalRequestToPending)
 * @param {string|number} requestId
 */
export const resetRoomTypeApprovalRequestToPending = async (requestId) => {
  try {
    const response = await apiClient.post(
      '',
      { request_id: Number(requestId) },
      { params: { action: 'resetRoomTypeApprovalRequestToPending' } }
    );
    const d = response.data;
    if (d && (d.status === false || d.status === 'false')) {
      return { success: false, error: d.message || 'Reset failed', data: d };
    }
    return { success: true, data: d };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Reset failed',
      data: error.response?.data,
    };
  }
};

/**
 * Resend booking voucher / notifications (adminPanel.php?action=resendBookingVoucherAndNotifications)
 * @param {{ booking_id: number|string, scope: 'guest'|'hotelier'|'both', hotel_id?: number|string }} payload
 */
export const resendBookingVoucherAndNotifications = async (payload) => {
  try {
    const body = {
      booking_id:
        typeof payload.booking_id === 'number' ? payload.booking_id : Number(payload.booking_id),
      scope: String(payload.scope || '').trim(),
    };
    if (payload.hotel_id != null && String(payload.hotel_id).trim() !== '') {
      const raw = String(payload.hotel_id).trim();
      const num = Number(raw);
      body.hotel_id = Number.isNaN(num) ? raw : num;
    }
    const response = await apiClient.post('', body, {
      params: { action: 'resendBookingVoucherAndNotifications' },
    });
    const d = response.data;
    if (d && (d.status === false || d.status === 'false')) {
      return {
        success: false,
        error: d.message || d.messgae || 'Resend failed',
        data: d,
      };
    }
    return { success: true, data: d };
  } catch (error) {
    return {
      success: false,
      error:
        error.response?.data?.message ||
        error.response?.data?.messgae ||
        error.message ||
        'Resend failed',
      data: error.response?.data,
    };
  }
};

export const getSupportBookingDetails = async (payload) => {
  try {
    const body = {};
    if (payload.booking_id != null && payload.booking_id !== '') {
      body.booking_id = typeof payload.booking_id === 'number' ? payload.booking_id : Number(payload.booking_id);
    }
    if (payload.booking_code != null && String(payload.booking_code).trim() !== '') {
      body.booking_code = String(payload.booking_code).trim();
    }
    const response = await apiClient.post('', body, {
      params: { action: 'getSupportBookingDetails' },
    });
    const d = response.data;
    if (d && (d.status === false || d.status === 'false')) {
      return {
        success: false,
        error: d.message || d.messgae || 'Failed to load booking details',
        data: d,
        raw: d,
      };
    }
    // Support both { data: { guest_booking, property_and_host } } and root-level blocks
    const inner = d?.data && typeof d.data === 'object' ? d.data : {};
    return {
      success: true,
      guest_booking: inner?.guest_booking ?? d?.guest_booking ?? null,
      property_and_host: inner?.property_and_host ?? d?.property_and_host ?? null,
      raw: d,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error.response?.data?.message ||
        error.response?.data?.messgae ||
        error.message ||
        'Failed to load booking details',
      data: error.response?.data,
      raw: error.response?.data,
    };
  }
};

// ==================== GST STATUS ====================

/**
 * List hotels with pending GST status (adminPanel.php?action=getPendingGstHotels)
 * @returns {Promise<{ success: boolean, hotels?: array, count?: number, error?: string, data?: object }>}
 */
export const getPendingGstHotels = async () => {
  try {
    const response = await apiClient.get('', {
      params: { action: 'getPendingGstHotels' },
      timeout: 30000,
    });
    const d = response.data;
    if (d && (d.status === false || d.status === 'false')) {
      return {
        success: false,
        error: d.message || 'Failed to fetch pending GST hotels',
        hotels: [],
        count: 0,
        data: d,
      };
    }
    const hotels = Array.isArray(d?.hotels) ? d.hotels : [];
    return {
      success: true,
      hotels,
      count: d?.count ?? hotels.length,
      data: d,
    };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to fetch pending GST hotels',
      hotels: [],
      count: 0,
      data: error.response?.data,
    };
  }
};

/**
 * Transfer booking — fetch room types for target hotel
 * (adminPanel.php?action=transferBookingGetRooms)
 * @param {{ booking_code?: string, booking_id?: number|string, new_hotel_id: number|string }} payload
 */
export const transferBookingGetRooms = async (payload) => {
  try {
    const body = { new_hotel_id: Number(payload.new_hotel_id) };
    if (payload.booking_id != null && payload.booking_id !== '') {
      body.booking_id = typeof payload.booking_id === 'number' ? payload.booking_id : Number(payload.booking_id);
    }
    if (payload.booking_code != null && String(payload.booking_code).trim() !== '') {
      body.booking_code = String(payload.booking_code).trim();
    }
    const response = await apiClient.post('', body, {
      params: { action: 'transferBookingGetRooms' },
      timeout: 60000,
    });
    const d = response.data;
    if (d && (d.status === false || d.status === 'false')) {
      return {
        success: false,
        error: d.message || 'Failed to fetch room types for transfer',
        data: d,
      };
    }
    const inner = d?.data && typeof d.data === 'object' ? d.data : d;
    return {
      success: true,
      data: inner,
      message: d?.message,
      raw: d,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch room types for transfer',
      data: error.response?.data,
    };
  }
};

/**
 * Find nearby hotels for a booking transfer
 * (adminPanel.php?action=transferBookingFindNearbyHotels)
 * @param {{ booking_code?: string, booking_id?: number|string, limit?: number, max_km?: number }} payload
 */
export const transferBookingFindNearbyHotels = async (payload) => {
  try {
    const body = {};
    if (payload.booking_id != null && payload.booking_id !== '') {
      body.booking_id = typeof payload.booking_id === 'number' ? payload.booking_id : Number(payload.booking_id);
    }
    if (payload.booking_code != null && String(payload.booking_code).trim() !== '') {
      body.booking_code = String(payload.booking_code).trim();
    }
    if (payload.limit != null) body.limit = Number(payload.limit);
    if (payload.max_km != null) body.max_km = Number(payload.max_km);

    const response = await apiClient.post('', body, {
      params: { action: 'transferBookingFindNearbyHotels' },
      timeout: 60000,
    });
    const d = response.data;
    if (d && (d.status === false || d.status === 'false')) {
      return {
        success: false,
        error: d.message || 'Failed to find nearby hotels',
        data: d,
      };
    }
    const inner = d?.data && typeof d.data === 'object' ? d.data : d;
    return {
      success: true,
      data: inner,
      message: d?.message,
      raw: d,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error.response?.data?.message ||
        error.message ||
        'Failed to find nearby hotels',
      data: error.response?.data,
    };
  }
};

/**
 * Transfer booking — confirm move to new hotel + room type
 * (adminPanel.php?action=transferBookingConfirm)
 * @param {{ booking_code?: string, booking_id?: number|string, new_hotel_id: number|string, room_type_id: number|string, guests?: number, room_count?: number, reason?: string }} payload
 */
export const transferBookingConfirm = async (payload) => {
  try {
    const body = {
      new_hotel_id: Number(payload.new_hotel_id),
      room_type_id: Number(payload.room_type_id),
    };
    if (payload.booking_id != null && payload.booking_id !== '') {
      body.booking_id = typeof payload.booking_id === 'number' ? payload.booking_id : Number(payload.booking_id);
    }
    if (payload.booking_code != null && String(payload.booking_code).trim() !== '') {
      body.booking_code = String(payload.booking_code).trim();
    }
    if (payload.guests != null && payload.guests !== '') {
      body.guests = Number(payload.guests);
    }
    if (payload.room_count != null && payload.room_count !== '') {
      body.room_count = Number(payload.room_count);
    }
    if (payload.reason != null && String(payload.reason).trim() !== '') {
      body.reason = String(payload.reason).trim();
    }
    const response = await apiClient.post('', body, {
      params: { action: 'transferBookingConfirm' },
      timeout: 60000,
    });
    const d = response.data;
    if (d && (d.status === false || d.status === 'false')) {
      return {
        success: false,
        error: d.message || 'Transfer failed',
        data: d,
      };
    }
    const inner = d?.data && typeof d.data === 'object' ? d.data : d;
    return {
      success: true,
      data: inner,
      message: d?.message || 'Booking transferred successfully',
      raw: d,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error.response?.data?.message ||
        error.message ||
        'Transfer failed',
      data: error.response?.data,
    };
  }
};

/**
 * Update hotel GST status (adminPanel.php?action=updateHotelGstStatus)
 * @param {number|string} hotelId
 * @param {'pending'|'active'|'rejected'} gstStatus
 */
export const updateHotelGstStatus = async (hotelId, gstStatus) => {
  try {
    const response = await apiClient.post(
      '',
      { hotel_id: Number(hotelId), gst_status: gstStatus },
      { params: { action: 'updateHotelGstStatus' }, timeout: 20000 }
    );
    const d = response.data;
    if (d && (d.status === false || d.status === 'false')) {
      return {
        success: false,
        error: d.message || 'Failed to update GST status',
        data: d,
      };
    }
    return { success: true, data: d, message: d?.message };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to update GST status',
      data: error.response?.data,
    };
  }
};

/**
 * Hotels pending Stayflexi onboard (sf_onboard = no)
 * (adminPanel.php?action=getHotelsPendingSfOnboard)
 * @param {{ q?: string, limit?: number }} [params]
 */
export const getHotelsPendingSfOnboard = async (params = {}) => {
  try {
    const query = {};
    if (params.q != null && String(params.q).trim() !== '') {
      query.q = String(params.q).trim();
    }
    if (params.limit != null) {
      query.limit = Number(params.limit);
    }
    const response = await apiClient.get('', {
      params: { action: 'getHotelsPendingSfOnboard', ...query },
      timeout: 60000,
    });
    const d = response.data;
    if (d && (d.status === false || d.status === 'false')) {
      return {
        success: false,
        error: d.message || 'Failed to load pending Stayflexi onboard hotels',
        hotels: [],
        count: 0,
        data: d,
      };
    }
    const hotels = Array.isArray(d?.data) ? d.data : Array.isArray(d?.hotels) ? d.hotels : [];
    return {
      success: true,
      hotels,
      count: d?.count ?? hotels.length,
      data: d,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error.response?.data?.message ||
        error.message ||
        'Failed to load pending Stayflexi onboard hotels',
      hotels: [],
      count: 0,
      data: error.response?.data,
    };
  }
};

/**
 * Stayflexi onboard decision / location update
 * (adminPanel.php?action=approveHotelSfOnboard)
 *
 * @param {number|string|{ hotel_id: number|string, decision?: 'approve'|'rejected', location_point?: string, location?: string, embed_map?: string, embed_map_url?: string }} payload
 * - Approve: { hotel_id } or { hotel_id, decision: 'approve' }
 * - Reject: { hotel_id, decision: 'rejected' }
 * - Save location only (no decision): { hotel_id, location_point, location? }
 * - Approve/reject + location: include decision + location fields
 */
export const approveHotelSfOnboard = async (payload) => {
  try {
    const body =
      payload != null && typeof payload === 'object'
        ? { hotel_id: Number(payload.hotel_id) }
        : { hotel_id: Number(payload) };

    if (payload != null && typeof payload === 'object') {
      if (payload.decision === 'approve' || payload.decision === 'rejected') {
        body.decision = payload.decision;
      }
      if (payload.location_point != null && String(payload.location_point).trim() !== '') {
        body.location_point = String(payload.location_point).trim();
      }
      const embed =
        payload.location ?? payload.embed_map ?? payload.embed_map_url;
      if (embed != null && String(embed).trim() !== '') {
        body.location = String(embed).trim();
      }
    }

    const response = await apiClient.post('', body, {
      params: { action: 'approveHotelSfOnboard' },
      timeout: 120000,
    });
    const d = response.data;
    if (d && (d.status === false || d.status === 'false')) {
      const onboardMsg = d?.onboard_result?.message;
      return {
        success: false,
        error: [d.message, onboardMsg].filter(Boolean).join(' — ') || 'Stayflexi onboard request failed',
        data: d,
        skipped: false,
      };
    }
    return {
      success: true,
      message: d?.message || 'Stayflexi onboard request completed.',
      data: d,
      skipped: d?.skipped === true,
      hotel_id: d?.hotel_id ?? body.hotel_id,
    };
  } catch (error) {
    const errData = error.response?.data;
    const onboardMsg = errData?.onboard_result?.message;
    return {
      success: false,
      error:
        [errData?.message, onboardMsg].filter(Boolean).join(' — ') ||
        error.message ||
        'Stayflexi onboard request failed',
      data: errData,
      skipped: false,
    };
  }
};

/**
 * Stayflexi hotel address data (GHA)
 * (adminPanel.php?action=getStayflexiHotelAddressData)
 * @param {{ hotel_id?: number|string, hotel_id_from?: number|string, hotel_id_to?: number|string, range?: string }} params
 */
export const getStayflexiHotelAddressData = async (params = {}) => {
  try {
    const query = {};
    if (params.hotel_id != null && String(params.hotel_id).trim() !== '') {
      query.hotel_id = Number(params.hotel_id);
    }
    if (params.hotel_id_from != null && String(params.hotel_id_from).trim() !== '') {
      query.hotel_id_from = Number(params.hotel_id_from);
    }
    if (params.hotel_id_to != null && String(params.hotel_id_to).trim() !== '') {
      query.hotel_id_to = Number(params.hotel_id_to);
    }
    if (params.range != null && String(params.range).trim() !== '') {
      query.range = String(params.range).trim();
    }

    const response = await apiClient.get('', {
      params: { action: 'getStayflexiHotelAddressData', ...query },
      timeout: 120000,
    });
    const d = response.data;
    if (d && (d.status === false || d.status === 'false')) {
      return {
        success: false,
        error: d.message || 'Failed to load Stayflexi hotel address data',
        hotels: [],
        count: 0,
        filters: d?.filters || null,
        data: d,
      };
    }
    const hotels = Array.isArray(d?.data) ? d.data : [];
    return {
      success: true,
      message: d?.message,
      hotels,
      count: d?.count ?? hotels.length,
      filters: d?.filters ?? null,
      data: d,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error.forbiddenMessage ||
        error.response?.data?.message ||
        error.message ||
        'Failed to load Stayflexi hotel address data',
      hotels: [],
      count: 0,
      filters: null,
      data: error.response?.data,
      required_feature: error.required_feature || error.response?.data?.required_feature,
    };
  }
};

// ==================== ADMIN RBAC (superadmin manage users) ====================

const unwrapRbac = (response, fallbackError) => {
  const d = response.data;
  if (d && (d.status === false || d.status === 'false' || d.success === false)) {
    return {
      success: false,
      error: d.message || fallbackError,
      data: d,
      required_feature: d.required_feature,
    };
  }
  return { success: true, data: d?.data ?? d, raw: d, message: d?.message };
};

const rbacCatch = (error, fallbackError) => ({
  success: false,
  error:
    error.forbiddenMessage ||
    error.response?.data?.message ||
    error.message ||
    fallbackError,
  data: error.response?.data,
  required_feature: error.required_feature || error.response?.data?.required_feature,
  status: error.response?.status,
});

/** Feature catalog grouped by bucket */
export const getAdminFeatureCatalog = async () => {
  try {
    const response = await apiClient.get('', {
      params: { action: 'getAdminFeatureCatalog' },
      timeout: 20000,
    });
    const result = unwrapRbac(response, 'Failed to load feature catalog');
    if (!result.success) return result;
    const payload = result.data || {};
    return {
      success: true,
      grouped: payload.grouped || payload || {},
      features: payload.features || [],
      data: payload,
      message: result.message,
    };
  } catch (error) {
    return rbacCatch(error, 'Failed to load feature catalog');
  }
};

/** List admin users (admin table) */
export const listAdminUsers = async () => {
  try {
    const response = await apiClient.get('', {
      params: { action: 'listAdminUsers' },
      timeout: 30000,
    });
    const result = unwrapRbac(response, 'Failed to list admin users');
    if (!result.success) return result;
    const list = Array.isArray(result.data)
      ? result.data
      : result.data?.users || result.raw?.users || [];
    return { success: true, users: list, data: result.data, message: result.message };
  } catch (error) {
    return rbacCatch(error, 'Failed to list admin users');
  }
};

/**
 * Create limited admin (type=admin always — do not send type:superadmin)
 * @param {{ name, email, phone, password, confirm_password|password_confirm, features?: string[], hotels?: any[] }} payload
 */
export const createAdminUser = async (payload) => {
  try {
    const body = {
      name: String(payload.name || '').trim(),
      email: String(payload.email || '').trim(),
      phone: String(payload.phone || '').trim(),
      password: payload.password,
      confirm_password: payload.confirm_password || payload.password_confirm || payload.password,
      features: Array.isArray(payload.features) ? payload.features : [],
      hotels: Array.isArray(payload.hotels) ? payload.hotels : [],
    };
    const response = await apiClient.post('', body, {
      params: { action: 'createAdminUser' },
      timeout: 30000,
    });
    return unwrapRbac(response, 'Failed to create admin user');
  } catch (error) {
    return rbacCatch(error, 'Failed to create admin user');
  }
};

/**
 * Update admin access (features, phone, password, is_active)
 * Alias: setAdminUserAccess
 */
export const updateAdminUser = async (payload) => {
  try {
    const body = {
      id: payload.id,
      ...(payload.name != null ? { name: String(payload.name).trim() } : {}),
      ...(payload.phone != null ? { phone: String(payload.phone).trim() } : {}),
      ...(payload.features != null ? { features: payload.features } : {}),
      ...(payload.hotels != null ? { hotels: payload.hotels } : {}),
      ...(payload.is_active != null ? { is_active: payload.is_active } : {}),
      ...(payload.password ? { password: payload.password } : {}),
      ...(payload.confirm_password || payload.password_confirm
        ? {
            confirm_password:
              payload.confirm_password || payload.password_confirm,
          }
        : {}),
    };
    const response = await apiClient.post('', body, {
      params: { action: 'updateAdminUser' },
      timeout: 30000,
    });
    return unwrapRbac(response, 'Failed to update admin user');
  } catch (error) {
    return rbacCatch(error, 'Failed to update admin user');
  }
};

export const setAdminUserAccess = updateAdminUser;

/** Deactivate admin user (revokes sessions) */
export const deactivateAdminUser = async (id) => {
  try {
    const response = await apiClient.post(
      '',
      { id },
      {
        params: { action: 'deactivateAdminUser' },
        timeout: 20000,
      }
    );
    return unwrapRbac(response, 'Failed to deactivate admin user');
  } catch (error) {
    return rbacCatch(error, 'Failed to deactivate admin user');
  }
};

/** Current admin profile (also available via authApi) */
export const getAdminProfile = async () => {
  try {
    const response = await apiClient.get('', {
      params: { action: 'getAdminProfile' },
      timeout: 20000,
    });
    return unwrapRbac(response, 'Failed to load admin profile');
  } catch (error) {
    return rbacCatch(error, 'Failed to load admin profile');
  }
};

export default adminPanelApi; 